import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';

import { type ParsedCountry } from 'react-international-phone';
import GradientButton from '../../components/gradientButton/GradientButton';
import RegistrationPage from '../../components/layouts/RegistrationPage';

import imIdL from '../../assets/svgs/imId-l.svg';
import imIdSm from '../../assets/svgs/imId-sm.svg';
import { checkImIdEnabled } from '../../utils/featureFlags';
import { isValidPhone } from '../../utils/validation';

import 'react-international-phone/style.css';
import PageHeader from '../../components/PageHeader';
import PhoneField from '../../components/form/PhoneField';

const ImIdVerification = () => {
    const { t } = useTranslation();
    const [phone, setPhone] = useState('');
    const [dialCode, setDialCode] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        if (!checkImIdEnabled()) {
            navigate('/verification-options', { replace: true });
        }
    }, [navigate]);

    const handlePhoneChange = (
        _: string,
        meta: {
            country: ParsedCountry;
            inputValue: string;
        }
    ) => {
        const { dialCode: countryDialCode } = meta.country;

        const digitsOnly = meta.inputValue.replace(/\D/g, '');
        const userPhone = digitsOnly.startsWith(countryDialCode)
            ? digitsOnly.slice(countryDialCode.length)
            : digitsOnly;

        setDialCode(countryDialCode);
        setPhone(userPhone);
    };

    const isPhoneValid = isValidPhone(phone);

    const handleIdentifyWithMyId = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!isPhoneValid) return;

        const phoneNumber = `+${dialCode}${phone}`;
        navigate(`/imid-verification-pending?phoneNumber=${encodeURIComponent(phoneNumber)}`);
    };

    const handleBack = () => navigate('/verification-options');

    return (
        <RegistrationPage
            headerProps={{ showBack: true, onBack: handleBack }}
            contentClassName="max-w-full md:max-w-[21rem] md:mx-auto !px-0"
            stickyFooter
            footer={
                <GradientButton
                    type="submit"
                    form="imid-verification-form"
                    disabled={phone?.length < 8}
                    wrapperClassName="max-w-full md:max-w-[21rem] mx-auto mt-10"
                >
                    <Trans
                        i18nKey="verificationOptions.identifyWithImId"
                        components={[
                            <img key="imid-sm" src={imIdSm} alt="imID" className="ml-1" />,
                        ]}
                    />
                </GradientButton>
            }
        >
            <PageHeader
                title={t('verificationOptions.identificationWithImID')}
                lead={t('verificationOptions.enterPhoneNumberLinked')}
                imgSrc={imIdL}
                imgClassName="h-8 w-auto lg:h-8 lg:w-auto"
            />
            <form
                id="imid-verification-form"
                className="flex flex-col items-center gap-10 mt-10"
                onSubmit={handleIdentifyWithMyId}
            >
                <PhoneField
                    id="phone"
                    name="phone"
                    autoComplete="tel"
                    defaultCountry="am"
                    forceDialCode={true}
                    hideDropdown={true}
                    autoFocus
                    countrySelectorStyleProps={{
                        dropdownArrowStyle: { display: 'none' },
                        buttonClassName: 'p-3 !h-12 border-r-0',
                    }}
                    inputProps={{ id: 'imId-phone' }}
                    className="w-full"
                    inputClassName="w-full !h-12 !border-l-0 !text-sm !leading-5"
                    onChange={handlePhoneChange}
                />
            </form>
        </RegistrationPage>
    );
};

export default ImIdVerification;
